import { useState, useEffect } from "react";
import T from "../theme/colors";

const API_BASE = process.env.REACT_APP_API_BASE_URL
  || "https://darshanapi.solapurmall.com/api";

async function publicFetch(path, options = {}) {
  const res = await fetch(API_BASE + path, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  let data;
  try { data = await res.json(); } catch { data = { message: `Server error (${res.status})` }; }
  return { ok: res.ok, status: res.status, data };
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh", background: "var(--bg-app)", padding: "32px 16px",
    display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "system-ui, sans-serif"
  },
  card: {
    background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
    padding: "28px 32px", marginBottom: 18, maxWidth: 680, width: "100%"
  },
  label: {
    fontSize: 12, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase",
    letterSpacing: "0.07em", display: "block", marginBottom: 4
  },
  value: { fontSize: 15, fontWeight: 600, color: "var(--text-0)" },
  heading: { fontSize: 17, fontWeight: 700, color: "var(--text-0)", marginBottom: 6 },
  hint: { fontSize: 14, color: "var(--text-1)", lineHeight: 1.6, marginBottom: 18 },
  input: {
    width: "100%", padding: "11px 14px", borderRadius: 8, fontSize: 16,
    border: "1.5px solid #ddd", outline: "none", boxSizing: "border-box",
    background: "var(--bg-hover)", color: "var(--text-0)"
  },
  btnGreen: {
    width: "100%", padding: "13px", borderRadius: 8, fontSize: 17, fontWeight: 700,
    border: "none", cursor: "pointer", background: "var(--success)", color: "#fff",
    marginTop: 14, transition: "opacity .15s"
  },
  error: {
    padding: "9px 14px", borderRadius: 7, background: "var(--danger-bg)",
    border: "1px solid #fcc", color: "var(--danger)", fontSize: 14, marginBottom: 12
  },
  tag: (bg, color) => ({
    display: "inline-block", padding: "3px 12px", borderRadius: 14,
    fontSize: 13, fontWeight: 700, background: bg, color
  }),
};

// ── Status Badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status, expired }) {
  const map = {
    DRAFT: { bg: "var(--bg-app)", color: "var(--text-2)", label: "Draft" },
    SENT: { bg: "#fff8e6", color: "#d68910", label: "Awaiting Acceptance" },
    ACCEPTED: { bg: "var(--success-bg)", color: "var(--success)", label: "Accepted ✓" },
    REJECTED: { bg: "var(--danger-bg)", color: "var(--danger)", label: "Rejected" },
  };
  const s = (expired && status === "SENT")
    ? { bg: "var(--danger-bg)", color: "var(--danger)", label: "Expired" }
    : (map[status] || map.DRAFT);
  return <span style={S.tag(s.bg, s.color)}>{s.label}</span>;
}

// ── Info Item ──────────────────────────────────────────────────────────────────
function InfoItem({ label, value, warn }) {
  return (
    <div>
      <span style={S.label}>{label}</span>
      <span style={{ ...S.value, color: warn ? "var(--danger)" : "#1a1a2e" }}>{value || "—"}</span>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AcceptQuotationPage({ token }) {
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState("");
  const [name, setName] = useState("");
  const [nameErr, setNameErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) { setFetchErr("Invalid link."); setLoading(false); return; }
    publicFetch(`/public/quotation/${token}`)
      .then(({ ok, data }) => {
        if (!ok || !data.data) {
          setFetchErr(data.message || "Quotation not found or link is invalid.");
        } else {
          setQuotation(data.data);
          if (data.data.alreadyAccepted) setAccepted(true);
        }
      })
      .catch(err => setFetchErr("Network error — could not reach server. " + (err?.message || "")))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!name.trim()) { setNameErr("Please enter your full name to confirm."); return; }
    setNameErr(""); setAccepting(true);
    try {
      const { ok, data } = await publicFetch(`/public/quotation/${token}/accept`, {
        method: "POST",
        body: JSON.stringify({ acceptedByName: name.trim() }),
      });
      if (ok && data.data) {
        setQuotation(data.data);
        setAccepted(true);
      } else {
        setNameErr(data.message || "Could not accept. Please try again.");
      }
    } catch {
      setNameErr("Network error. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, justifyContent: "center" }}>
      <div style={{ fontSize: 16, color: "var(--text-2)" }}>Loading quotation…</div>
    </div>
  );

  // ── Error / not found ──────────────────────────────────────────────────────
  if (fetchErr) return (
    <div style={{ ...S.page, justifyContent: "center" }}>
      <div style={{ ...S.card, textAlign: "center", borderTop: "4px solid #e74c3c" }}>
        <div style={{ fontSize: 46, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-0)", marginBottom: 8 }}>Invalid Link</div>
        <div style={{ fontSize: 15, color: "var(--text-2)", marginBottom: 12 }}>{fetchErr}</div>
        <div style={{ fontSize: 13, color: "var(--text-2)", fontFamily: "monospace", wordBreak: "break-all" }}>
          API: {API_BASE}/public/quotation/{token ? token.substring(0, 8) + "…" : "?"}
        </div>
      </div>
    </div>
  );

  const q = quotation;
  const isExpired = q?.expired && !accepted;
  const canAccept = !accepted && !isExpired && q?.status !== "REJECTED";

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ maxWidth: 680, width: "100%", marginBottom: 12 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: "var(--text-2)",
          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4
        }}>
          {q?.firmName}
        </div>
        <div style={{ fontSize: 25, fontWeight: 800, color: "var(--text-0)" }}>Quotation Review</div>
        <div style={{ fontSize: 15, color: "var(--text-2)", marginTop: 4 }}>
          Please review the details below and accept if you agree.
        </div>
      </div>

      {/* Summary card */}
      <div style={S.card}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20
        }}>
          <div>
            <span style={S.label}>Quotation Number</span>
            <div style={{
              fontSize: 25, fontWeight: 800, color: "var(--text-0)",
              fontFamily: "monospace"
            }}>{q?.quotationNumber}</div>
          </div>
          <StatusBadge status={q?.status} expired={isExpired} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
          <InfoItem label="Customer" value={q?.customerName} />
          <InfoItem label="Quotation Date" value={q?.quotationDate} />
          <InfoItem label="Valid Until" value={q?.validUntil} warn={isExpired} />
        </div>

        {q?.grandTotal > 0 && (
          <div style={{
            padding: "12px 16px", borderRadius: 8,
            background: "var(--info-bg)", border: "1px solid #cce0ff",
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>Grand Total</span>
            <span style={{ fontSize: 25, fontWeight: 800, color: "var(--text-0)" }}>
              ₹{Number(q.grandTotal).toLocaleString("en-IN")}
            </span>
          </div>
        )}
      </div>

      {/* Notes + Terms */}
      {(q?.notes || q?.termsAndConditions) && (
        <div style={S.card}>
          {q.notes && (
            <div style={{ marginBottom: q.termsAndConditions ? 16 : 0 }}>
              <div style={S.heading}>Notes</div>
              <div style={{ fontSize: 15, color: "var(--text-1)", lineHeight: 1.7 }}>{q.notes}</div>
            </div>
          )}
          {q.termsAndConditions && (
            <div>
              <div style={S.heading}>Terms &amp; Conditions</div>
              <div style={{
                fontSize: 14, color: "var(--text-1)", lineHeight: 1.8,
                whiteSpace: "pre-wrap"
              }}>{q.termsAndConditions}</div>
            </div>
          )}
        </div>
      )}

      {/* Accept section */}
      {canAccept && (
        <div style={{ ...S.card, borderTop: "4px solid #27ae60" }}>
          <div style={S.heading}>Accept this Quotation</div>
          <div style={S.hint}>
            By clicking <strong>Accept Quotation</strong> below you confirm that you have reviewed
            the above details and agree to proceed with the order as stated.
          </div>

          <label style={S.label}>Your Full Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); setNameErr(""); }}
            placeholder="Enter your full name to confirm acceptance"
            style={{ ...S.input, borderColor: nameErr ? "#e74c3c" : "#ddd" }}
          />

          {nameErr && <div style={{ ...S.error, marginTop: 10 }}>{nameErr}</div>}

          <button
            style={{ ...S.btnGreen, opacity: accepting ? 0.7 : 1 }}
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? "Accepting…" : "✅  Accept Quotation"}
          </button>
        </div>
      )}

      {/* Thank you */}
      {accepted && (
        <div style={{ ...S.card, borderTop: "4px solid #27ae60", textAlign: "center" }}>
          <div style={{ fontSize: 60, marginBottom: 12 }}>🎉</div>
          <div style={{ fontSize: 23, fontWeight: 800, color: "var(--success)", marginBottom: 10 }}>
            Quotation Accepted!
          </div>
          <div style={{ fontSize: 16, color: "var(--text-1)", lineHeight: 1.8 }}>
            Thank you, <strong>{q?.acceptedByName || name}</strong>!<br />
            Quotation <strong>{q?.quotationNumber}</strong> has been successfully accepted.
          </div>
          {q?.acceptedAt && (
            <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 10 }}>
              Accepted on {q.acceptedAt}
            </div>
          )}
        </div>
      )}

      {/* Expired */}
      {isExpired && (
        <div style={{ ...S.card, borderTop: "4px solid #e74c3c", textAlign: "center" }}>
          <div style={{ fontSize: 46, marginBottom: 12 }}>⏰</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#e74c3c", marginBottom: 8 }}>
            Quotation Expired
          </div>
          <div style={{ fontSize: 15, color: "var(--text-1)" }}>
            This quotation was valid until <strong>{q?.validUntil}</strong>.<br />
            Please contact us for an updated quotation.
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 8 }}>
        Darshan Uniforms · Garment ERP · Developed by SMIT{q?.firmName ? ` · ${q.firmName}` : ""}
      </div>
    </div>
  );
}